import React, { useState } from 'react';
import FirebaseService from '@/lib/firebase';

// Define TypeScript interfaces for the component
interface ChatMessage {
  sender: string;
  content: string;
  timestamp?: number;
  userName?: string;
  id?: string;
  [key: string]: any; // Allow for additional properties
}

interface DownloadInfo {
  url: string;
  filename: string;
  format: string;
  timestamp: number;
}

// Define allowed export formats
export type ExportFormat = 'docx' | 'pdf' | 'html' | 'pptx'; // Added 'pptx'

interface QuartoExportButtonProps {
  apiEndpoint?: string;
  reportData?: Record<string, any>;
  buttonText?: string;
  method?: string;
  exportFormat?: ExportFormat; // Use the defined type
  message: ChatMessage;
  className?: string;
  extractTextOnly?: boolean;
  onSuccess?: (result?: any) => void;
  onError?: (error?: Error) => void;
  variant?: 'icon' | 'button';
  showLabel?: boolean;
  bucketName?: string;
  onExportComplete?: (messageId: string, downloadInfo: DownloadInfo) => void;
}

function QuartoExportButton({
    apiEndpoint = '/api/quarto',
    reportData = {},
    buttonText = 'Export',
    method = 'POST',
    exportFormat = 'docx', // Default format
    message,
    className = '',
    extractTextOnly = true,
    onSuccess = () => {},
    onError = () => {},
    variant = 'icon',
    showLabel = false,
    bucketName = '',
    onExportComplete = () => {}
}: QuartoExportButtonProps) {
    // State for loading indicator, error messages, and status messages
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState('');
    const [progress, setProgress] = useState(0);

    // Generate a consistent message ID for tracking exports
    // Uses btoa for a simple hash-like ID, removing padding characters.
    const messageId = message.id || `msg-${btoa(message.content.substring(0, 50)).replace(/=/g, '')}-${message.timestamp}`;

    // Function to prepare message content for export
    const prepareMessageForExport = (): Record<string, any> => {
        if (!message) {
            return {};
        }

        // Extract plain text from HTML if needed
        let content = message.content;
        if (extractTextOnly && content) {
            // Simple HTML tag removal for <thinking> blocks.
            // For more complex HTML, a dedicated library might be better.
            content = content.replace(/<thinking>[\s\S]*?<\/thinking>/g, '').trim();
        }

        return {
            content,
            timestamp: message.timestamp,
            sender: message.sender,
            userName: message.userName
            // Add any other relevant message properties here
        };
    };

    // Handle the export process when the button is clicked
    const handleExport = async () => {
        setIsLoading(true);
        setError(null);
        setStatusMessage('Preparing document...');
        setProgress(10); // Initial progress

        // Prepare the data payload for the API request
        const exportData = {
            ...reportData, // Include any additional report data
            message: prepareMessageForExport(), // Add the processed message
            format: exportFormat, // Specify the desired format (e.g., 'docx', 'pdf', 'pptx')
            bucketName: bucketName // Optional bucket name for storage
        };

        // Configure fetch options
        const fetchOptions = {
            method, // 'POST', 'GET', etc.
            headers: {
                // Only add Content-Type for methods that typically have a body
                ...(method !== 'GET' && method !== 'HEAD' && { 'Content-Type': 'application/json' }),
            },
            // Only add body for methods that typically have one
            ...(method !== 'GET' && method !== 'HEAD' && { body: JSON.stringify(exportData) })
        };

        try {
            setProgress(30);
            setStatusMessage('Generating document...');
            console.log('Sending Quarto export request:', apiEndpoint, fetchOptions);

            // Make the API request to the backend endpoint
            const response = await fetch(apiEndpoint, fetchOptions);

            // Check if the request was successful
            if (!response.ok) {
                let errorMsg = `Error ${response.status}: ${response.statusText}`;
                // Try to parse more detailed error from JSON response body
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.detail || errorData.error || errorMsg;
                } catch (e) {
                    // Ignore error if response is not JSON
                 }
                throw new Error(errorMsg); // Throw error to be caught below
            }

            setProgress(70);
            setStatusMessage('Finalizing document...');

            // Parse the successful JSON response
            const result = await response.json();
            const downloadUrl = result.download_url; // Expecting a download URL
            const filename = result.filename || `export.${exportFormat}`; // Use provided filename or generate one

            // Ensure a download URL was provided
            if (!downloadUrl) {
                throw new Error('Backend response did not include a download URL.');
            }

             // Create download info object
             const downloadInfo: DownloadInfo = {
                url: downloadUrl,
                filename: filename,
                format: exportFormat,
                timestamp: Date.now()
            };

            // Notify parent component about the completed export and provide download info
            onExportComplete(messageId, downloadInfo);

            // Track export in file history
            try {
                const user = FirebaseService.auth.currentUser;
                if (user && typeof window !== 'undefined' && (window as any)._assistantContext) {
                    const idToken = await user.getIdToken();
                    const assistantContext = (window as any)._assistantContext;

                    await fetch('/api/user/previews', {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${idToken}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            sourceUri: downloadUrl,
                            fileName: filename,
                            fileType: `application/${exportFormat}`,
                            activityType: 'export',
                            exportFormat: exportFormat,
                            assistantId: assistantContext.assistantId,
                            assistantName: assistantContext.assistantName,
                            assistantAvatar: assistantContext.assistantAvatar,
                            conversationUrl: `/assistant/${assistantContext.assistantId}`,
                            copySucceeded: true // Exports are already in user storage
                        })
                    });

                    console.log('✅ Tracked Quarto export:', filename);
                } else {
                    console.warn('⚠️ Could not track export - missing user or assistant context');
                }
            } catch (trackError) {
                console.error('Failed to track export:', trackError);
                // Don't fail the export if tracking fails
            }

            // --- Trigger Download or Open ---
            setProgress(90);
            setStatusMessage('Opening document...');

            // Always open the download URL in a new tab
            window.open(downloadUrl, '_blank');

            setProgress(100); // Mark as complete
            setStatusMessage('Document ready!');

            // Call the success callback provided by the parent component
            onSuccess(result); // Pass the full result if needed

            // Clear the status message after a short delay
            setTimeout(() => {
                setStatusMessage('');
                setProgress(0); // Reset progress
            }, 5000); // 5 seconds

        } catch (err) {
            // Handle any errors during the fetch or processing
            const error = err as Error;
            console.error('Quarto export failed:', error);
            setError(error.message || 'An unexpected error occurred.'); // Display error message
            setStatusMessage(''); // Clear any status message
            setProgress(0); // Reset progress
            onError(error); // Call the error callback
        } finally {
            setIsLoading(false); // Ensure loading state is turned off
        }
    };

    // Function to determine which icon to display based on format and loading state
    const getIcon = () => {
        // Show spinner while loading
        if (isLoading) {
            return (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            );
        }

        // Select icon based on the export format
        switch (exportFormat.toLowerCase()) {
            case 'pdf':
                // PDF Icon (Document with lines)
                return (
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                );
            case 'html':
                 // HTML Icon (Code brackets)
                return (
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                );
            // *** ADDED PPTX CASE ***
            case 'pptx':
                 // PowerPoint Icon (Using the same generic document icon as DOCX for now)
                 // You could replace this with a more specific PowerPoint SVG icon if available
                 return (
                     <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                         <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                     </svg>
                 );
            case 'docx':
            default: // Default to DOCX icon
                // DOCX Icon (Generic document)
                return (
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                    </svg>
                );
        }
    };

    // Function to render status messages, progress bar, or error messages
    const renderStatusContent = () => {
        // Display error if present
        if (error) {
            return (
                <span className="text-red-600 font-medium text-xs whitespace-nowrap">
                    Error: {error}
                </span>
            );
        }

        // Display status message and progress bar if exporting
        if (statusMessage) {
            return (
                <div className="flex items-center space-x-2">
                    <span className="text-blue-700 font-medium text-xs whitespace-nowrap">{statusMessage}</span>
                    {/* Show progress bar only when loading and progress is between 0 and 100 */}
                    {isLoading && progress > 0 && progress < 100 && (
                        <div className="w-16 bg-blue-200 rounded-full h-1 overflow-hidden">
                            <div
                                className="bg-blue-600 h-1 rounded-full transition-all duration-300 ease-in-out"
                                style={{ width: `${progress}%` }} // Width driven by progress state
                            ></div>
                        </div>
                    )}
                </div>
            );
        }

        // Return null if no error or status message
        return null;
    };

    // --- Render Component ---

    // Render as a small icon button (variant='icon')
    if (variant === 'icon') {
        return (
            <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 ${className}`}> {/* Added gap-y for wrapping */}
                <button
                    onClick={handleExport}
                    disabled={isLoading}
                    title={`Export to ${exportFormat.toUpperCase()}`} // Dynamic title
                    className="p-1 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" // Added focus offset and disabled styles
                    aria-label={`Export to ${exportFormat.toUpperCase()}`} // Dynamic aria-label
                >
                    {getIcon()} {/* Display the appropriate icon */}
                    {/* Optionally show text label next to icon */}
                    {showLabel && !isLoading && (
                        <span className="ml-1 text-xs font-medium">{buttonText}</span>
                    )}
                     {showLabel && isLoading && (
                        <span className="ml-1 text-xs font-medium">Exporting...</span>
                    )}
                </button>

                {/* Render status/error messages inline with the icon button */}
                {(statusMessage || error) && renderStatusContent()}
            </div>
        );
    }

    // Render as a standard button with text (variant='button')
    return (
        <div className={`quarto-export-wrapper flex flex-col sm:flex-row sm:items-center gap-2 ${className}`}>
            <button
                onClick={handleExport}
                disabled={isLoading}
                className="inline-flex items-center justify-center px-3 py-1.5 rounded border border-gray-300 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm" // Enhanced styling
                aria-label={`Export to ${exportFormat.toUpperCase()}`} // Dynamic aria-label
            >
                <span className="mr-1.5">{getIcon()}</span> {/* Icon */}
                {/* Button text changes based on loading state */}
                {isLoading ? 'Exporting...' : buttonText}
            </button>

            {/* Render status/error messages below or beside the button */}
            {(statusMessage || error) && (
                <div className="mt-1 sm:mt-0 sm:ml-2"> {/* Adjust margin based on layout */}
                    {renderStatusContent()}
                </div>
            )}
        </div>
    );
}

export default QuartoExportButton;
